<template>
  <div class="tab-container">
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__supplier><div class="table-default__left">Supplier</div></template>
        <template #payment="{ row }"><div class="table-default__controls">
          <button
            v-if="!row.cashflow"
            v-tooltip="{ content: 'Create transaction', placement: 'top-end', offset: 10 }"
            @click="onShowCreateTransactionModal(row)"
          >
            <img src="@/assets/img/add-big-button-icon.svg" alt="Add">
          </button>
          <span v-else class="status-tag" style="background-color: #77b017">Paid</span>
        </div></template>
        <template #due_date="{ row }">{{ dateFormat(row.due_date) }}</template>
        <template #supplier="{ row }"><div class="table-default__left">{{ row.supplier.legal_name }}</div></template>
        <template #amount="{ row }">
          {{ new Intl.NumberFormat('ru-RU').format(row.amount) }} {{ row.currency.code }}
        </template>
        <template #invoice_number="{ row }">{{ row.invoice_number || '-' }}</template>
        <template #account="{ row }">
          <template v-if="row.payment_method.code === 'bank'">
            {{ row.bank_info ? `${row.bank_info.account.account_number} (${row.bank_info.account.bank.short_name})` : '-' }}
          </template>
          <template v-else-if="row.payment_method.code === 'mobile'">
            {{ row.mobile_info ? `${row.mobile_info.account.account_number} (${row.mobile_info.account.provider.name})` : '-' }}
          </template>
          <template v-else>-</template>
        </template>
        <template #items="{ row }">
          <button
            v-tooltip="{
              content: tooltipContentInvoiceItems(row.fund_request_items),
              placement: 'left',
              offset: 10,
              html: true,
              trigger: 'click',
              classes: 'tooltip-cashflow',
              targetClasses: 'tooltip-target'
            }"
          >
            {{ row.fund_request_items.length }} items
          </button>
        </template>
        <template #attachment="{ row }">
          <div class="table-default__controls">
            <button
              @click="onDownloadFile(row)"
              type="button"
              :disabled="!row.attachment"
            >
              <img src="@/assets/img/download-table-blue-icon.svg" alt="">
            </button>
          </div>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModalDelete(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="transaction-modal"
      :width="800"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('transaction-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Add transaction</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckTransactionForm" class="arrange-popup">
            <app-grid>
              <template #item-1>
                <app-form-group label="Account" required>
                  <app-select
                    v-model="form_transaction.account"
                    :options="account_options"
                    :clearable="false"
                    placeholder="Account"
                    :error="$v.form_transaction.account.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_transaction.account.$dirty && !$v.form_transaction.account.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Date" required>
                  <app-date-picker
                    v-model="form_transaction.date"
                    placeholder="Specify a date"
                    :error="$v.form_transaction.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_transaction.date.$dirty && !$v.form_transaction.date.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <div class="arrange-popup__part">
              <div class="arrange-popup__block-title arrange-popup__block-title--fz13"><span>Items</span></div>
              <div class="transfer-sortable transfer-sortable--cashflow transfer-sortable--full">
                <div v-for="(element, index) in form_transaction.items" :key="`own_item_${index}`" class="transfer-sortable__item transfer-sortable__item--mb-20">
                  <div class="transfer-sortable__row">
                    <app-form-group label="Amount" :label-for="`amount-${index}`" required class="form-group--no-indent">
                      <app-fin-number
                        v-model="element.amount_total"
                        :id="`amount-${index}`"
                        placeholder="0"
                        name="amount"
                        readonly
                      />
                    </app-form-group>
                    <app-form-group label="Item" class="form-group--no-indent" required>
                      <app-select
                        v-model="element.expense_item"
                        :options="expense_item_options"
                        label="name_expense"
                        readonly
                        placeholder="Transaction item"
                      />
                    </app-form-group>
                    <app-form-group label="Supplier" class="form-group--no-indent">
                      <app-select
                        v-model="form_transaction.supplier"
                        label="trade_name"
                        readonly
                        placeholder="Supplier"
                      />
                    </app-form-group>
                  </div>
                </div>
              </div>
            </div>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_transaction" :disabled="$v.form_transaction.$error">Add</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-invoice-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-invoice-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete invoice
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this invoice?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-invoice-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteInvoice" ref="delete_invoice">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteFinanceFundRequestsSupplierInvoice,
  getFinanceAccounts,
  getFinanceFundRequestsSupplierInvoiceList,
  getFinanceTransactionsItemsList,
  postFinanceFundRequestsCashflow,
} from '@/http'
import { format, parseISO } from 'date-fns'
import axios from 'axios'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'FinanceFundRequestsDetailInvoices',
  data() {
    return {
      page_loaded: false,
      table_preloader: false,
      table: {
        items: [],
        columns: [
          'due_date',
          'supplier',
          'amount',
          'invoice_number',
          'payment_method.name',
          'account',
          'items',
          'attachment',
          'controls'
        ],
        options: {
          headings: {
            'payment': 'Payment',
            'due_date': 'Due date',
            'supplier': 'Supplier',
            'amount': 'Amount',
            'invoice_number': 'Invoice number',
            'payment_method.name': 'Payment method',
            'account': 'Account',
            'items': 'Items',
            'attachment': 'Attachment',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      invoice_to_action: null,
      form_transaction: {},
      account_options: [],
      expense_item_options: [],
    }
  },
  validations: {
    form_transaction: {
      account: { required },
      date: { required },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_accountant_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('accountant_supervisor')
    },
  },
  created() {
    if (this.is_general_manager || this.is_accountant_supervisor) this.table.columns.unshift('payment')
    this.fetchItems()
    getFinanceTransactionsItemsList({ expense: true })
      .then(response => {
        this.expense_item_options = response.data
      })
    getFinanceAccounts({ outcome: true }).then(response => { this.account_options = response.data })
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      getFinanceFundRequestsSupplierInvoiceList(Number(this.$route.params.id), { page: this.pagination.page, page_size: 20 })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.table_preloader = false
          this.page_loaded = true
        })
    },
    onCheckTransactionForm() {
      this.$v.form_transaction.$touch()
      if (this.$v.form_transaction.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormTransaction()
      }
    },
    sendFormTransaction() {
      this.$refs.submit_transaction.preload = true
      const formData = { ...this.form_transaction }
      formData.account = formData.account.id
      formData.invoice = this.invoice_to_action
      formData.items = formData.items.map(({amount_total, expense_item}) => {
        return {
          amount: amount_total,
          item: expense_item.id,
          supplier: formData.supplier.id,
        }
      })
      delete formData.supplier
      postFinanceFundRequestsCashflow(Number(this.$route.params.id), formData)
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Transaction created',
          })
          this.$refs.submit_transaction.preload = false
          this.$modal.hide('transaction-modal')
        })
        .catch(() => { this.$refs.submit_transaction.preload = false })
    },
    onDeleteInvoice() {
      this.$refs.delete_invoice.preload = true
      deleteFinanceFundRequestsSupplierInvoice(Number(this.$route.params.id), this.invoice_to_action)
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Invoice deleted',
          })
          this.$refs.delete_invoice.preload = false
          this.$modal.hide('delete-invoice-modal')
        })
        .catch(() => { this.$refs.delete_invoice.preload = false })
    },
    onShowCreateTransactionModal(row) {
      this.invoice_to_action = row.id
      this.form_transaction = {
        items: row.fund_request_items,
        supplier: row.supplier,
        date: format(new Date(), 'yyyy-MM-dd'),
      }
      this.$v.form_transaction.$reset()
      this.$modal.show('transaction-modal')
    },
    onShowModalDelete(id) {
      this.invoice_to_action = id
      this.$modal.show('delete-invoice-modal')
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    tooltipContentInvoiceItems(items) {
      let content = ''
      items.forEach(({name, real_qty, measurement, real_price, amount_total}) => {
        content += `<span><b>Name:</b> ${name} <b>Amount:</b> ${new Intl.NumberFormat('ru-RU').format(real_qty)} ${measurement.name} x ${new Intl.NumberFormat('ru-RU').format(real_price)} = ${new Intl.NumberFormat('ru-RU').format(amount_total)}`
      })
      return content
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    onDownloadFile(row) {
      axios.get(row.attachment.file, { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          const extension = row.attachment.file_name.split('.').pop()
          const part_of_name = `${row.supplier.legal_name} - ${this.dateFormat(row.due_date)} - ${row.payment_method.name}`

          link.href = url
          link.setAttribute('download', `${part_of_name}.${extension}`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
        })
    },
  }
}
</script>
